import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { usePrevious, useUpdateEffect } from "react-use";

import analytics from "fond/analytics";
import { apiSlice, useGetVersionQuery, useGetVersionStatusQuery } from "fond/api";
import { AsyncOperationState } from "fond/async/redux";
import { useFeatureFlag } from "fond/featureFlags";
import { getCurrentProject } from "fond/project";
import { getWorkflow, StatusTypes } from "fond/project/redux";
import { Store, WorkflowStep } from "fond/types";
import { useAppDispatch } from "fond/utils/hooks";
import { getDefaultWorkflowQuality } from "fond/utils/project";
import { ErrorModal } from "fond/widgets";

import SolveErrors from "./errors/SolveErrors";
import SolveRefinements from "./refinements/SolveRefinements";
import { getDesignEdits } from "./designEdits";
import { cancelDismissError, load, run, unload } from "./redux";
import SolveControl from "./SolveControl";
import SolveEditing from "./SolveEditing";
import SolveProgress from "./SolveProgress";
import SolveStatus from "./SolveStatus";

interface IProps {
  readOnly?: boolean;
}

const SolvePanel: React.FC<IProps> = ({ readOnly = false }: IProps) => {
  const dispatch = useAppDispatch();
  const project = useSelector((state: Store) => getCurrentProject(state.project));
  const versionId = useSelector((state: Store) => state.project.versionId);
  const { data: versionStatus, isError: isVersionStatusError } = useGetVersionStatusQuery(versionId, { skip: !versionId });
  const { data: version } = useGetVersionQuery(versionId, { skip: !versionId });
  const { value: isRefinementButtonsEnabled } = useFeatureFlag("planner_refinement_buttons", false);

  const { runStatus, cancelStatus, updateDataStatus } = useSelector((state: Store) => state.solve);
  const status = updateDataStatus === AsyncOperationState.failure ? null : versionStatus?.Status;
  const prevStatus = usePrevious(status);

  useEffect(() => {
    // When a design transitions to complete (from another known status)
    // Update the layers & subscription information
    if (prevStatus && prevStatus !== status && status === "Complete") {
      dispatch(
        apiSlice.util.invalidateTags([
          { type: "Layers", id: versionId },
          { type: "Subscription", id: "LIST" },
          { type: "FeatureTotals", id: versionId },
        ])
      );
    }
  }, [dispatch, status, versionId]);

  useEffect(() => {
    dispatch(load());
    return () => {
      dispatch(unload());
    };
  }, [dispatch, versionId]);

  useUpdateEffect(() => {
    const jobIds: { ID: string; JobID: string }[] = getWorkflow(versionStatus)?.map((w: WorkflowStep) => ({ ID: w.ID, JobID: w.JobID }));

    const projectStatusToEventName = {
      [StatusTypes.Complete]: "Solve completed",
      [StatusTypes.Cancelled]: "Solve cancelled",
      [StatusTypes.Terminated]: "Solve error",
    } as const;

    if (project?.ID && versionStatus?.Status && versionStatus?.Status in projectStatusToEventName) {
      analytics.track(projectStatusToEventName[versionStatus.Status], { projectId: project.ID, jobIds });
    }
  }, [versionStatus?.Status]);

  const { hasEdits, ...appliedRefinements } = getDesignEdits(versionStatus);

  const showProgress = status && [StatusTypes.Running, StatusTypes.Cancelling, StatusTypes.Terminated].includes(status);
  const showHubEditing =
    versionStatus?.HasSolution && status && [StatusTypes.Complete, StatusTypes.Terminated].includes(status) && !version?.Architecture?.IsFlexNap;

  return (
    <>
      <SolveStatus status={status ?? null} isError={isVersionStatusError || updateDataStatus === AsyncOperationState.failure} />
      <SolveErrors errors={versionStatus?.Error ?? []} />
      {showProgress && <SolveProgress workflow={getWorkflow(versionStatus)} />}
      {showHubEditing &&
        (isRefinementButtonsEnabled ? (
          <SolveRefinements {...appliedRefinements} />
        ) : (
          <SolveEditing hasEditedEquipment={hasEdits} hasDropAssignments={appliedRefinements.hasDropAssignments} />
        ))}

      <SolveControl readOnly={readOnly} status={status} initialQuality={versionStatus?.WorkflowQuality ?? getDefaultWorkflowQuality(project)} />

      {runStatus === AsyncOperationState.failure && <ErrorModal onClose={() => dispatch(run.dismissError())} />}
      {cancelStatus === AsyncOperationState.failure && <ErrorModal onClose={() => dispatch(cancelDismissError())} />}
    </>
  );
};

SolvePanel.displayName = "SolvePanel";
export default SolvePanel;
